import React from 'react';

import { Chip } from '@appcharge/shared-ui';
import { EBadgeType, Offer } from 'common/contracts';
import { MIN_USD_PRICE, PRICE_VALUE_LIMIT } from 'constants/constants';
import {
  EAppearanceStatus,
  EAssetType,
  EProductPriority,
  EProductType,
  ESaleDiscountDisplayType,
  SortingDirection,
  SortingOffersValue,
  OfferType
} from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import { OffersSorting } from 'api/useOffers';
import { appearanceUtils } from 'utils/scheduleUtils';

import { EBundlesViewModel } from '@appcharge/shared-ui';

import {
  CLIENT_SIDE_SORT_VALUES,
  MAP_VIEW_MODEL_TO_BADGE_TYPE,
  OFFER_STATUS_CHIP_MAPPING,
  OFFER_STATUS_PRIORITY
} from './Offer.consts';
import {
  IApiProduct,
  IBaseOfferPayload,
  IDynamicOfferUi,
  IOfferBadge,
  IPriceDiscount,
  IProductProps,
  IProductSale,
  ISectionTabPayload,
  ISharedFormValues,
  OfferStatus
} from './Offer.types';

dayjs.extend(utc);

// --- Status ---

export const getOfferStatus = (offer: Offer): OfferStatus => {
  if (!offer.active) {
    return 'Inactive';
  }

  if (offer.schedule?.permanent && offer.schedule?.interval) {
    return 'Interval';
  }

  if (offer.schedule?.permanent) {
    return 'Permanent';
  }

  return 'Active';
};

export const renderOfferStatusChip = (offer: Offer): React.ReactElement => {
  const status = getOfferStatus(offer);

  return (
    <Chip
      variant="flat"
      status={OFFER_STATUS_CHIP_MAPPING[status]}
      className="capitalize"
    >
      {status}
    </Chip>
  );
};

// --- Schedule display ---

export const getScheduleDisplayValue = (offer: Offer): string => {
  const schedule = offer?.schedule;

  if (schedule?.permanent && schedule?.interval) {
    return appearanceUtils.getNextAppearanceByRefreshTime(schedule.interval);
  }

  if (schedule?.permanent && !schedule?.interval) {
    return '-';
  }

  const nextUpcomingTime = appearanceUtils.getNextUpcomingTime(
    schedule?.timeFrames
  );

  if (nextUpcomingTime) {
    return nextUpcomingTime;
  }

  return '-';
};

// --- API sorting fallback ---

interface ISortingWithNullable {
  sortValue: SortingOffersValue | null;
  direction: SortingDirection;
}

export const getApiSorting = (sorting: ISortingWithNullable): OffersSorting => {
  if (sorting.sortValue === null || CLIENT_SIDE_SORT_VALUES.has(sorting.sortValue)) {
    return {
      sortValue: SortingOffersValue.CREATED_AT,
      direction: SortingDirection.DESC
    };
  }

  return { sortValue: sorting.sortValue, direction: sorting.direction };
};

// --- Client-side sort functions ---

export const sortOffersByStatus = (
  offers: Offer[],
  direction: SortingDirection
): Offer[] => {
  const multiplier = direction === SortingDirection.ASC ? 1 : -1;

  return [...offers].sort((a: Offer, b: Offer): number => {
    return (
      multiplier *
      (OFFER_STATUS_PRIORITY[getOfferStatus(a)] -
        OFFER_STATUS_PRIORITY[getOfferStatus(b)])
    );
  });
};

export const sortOffersBySchedule = (
  offers: Offer[],
  direction: SortingDirection
): Offer[] => {
  return [...offers].sort((a: Offer, b: Offer): number => {
    const aIsPermanent = a.schedule?.permanent && !a.schedule?.interval;
    const bIsPermanent = b.schedule?.permanent && !b.schedule?.interval;

    if (aIsPermanent && !bIsPermanent) {
      return direction === SortingDirection.ASC ? -1 : 1;
    }

    if (!aIsPermanent && bIsPermanent) {
      return direction === SortingDirection.ASC ? 1 : -1;
    }

    const aTime = getScheduleNextTime(a);
    const bTime = getScheduleNextTime(b);

    if (direction === SortingDirection.ASC) {
      return aTime - bTime;
    }

    return bTime - aTime;
  });
};

const getScheduleNextTime = (offer: Offer): number => {
  const schedule = offer?.schedule;

  if (!schedule) {
    return 0;
  }

  if (schedule.permanent && schedule.interval) {
    const nextTime = appearanceUtils.getNextAppearanceByRefreshTime(
      schedule.interval
    );
    return dayjs.utc(nextTime).valueOf();
  }

  const nextUpcoming = appearanceUtils.getNextUpcomingTime(
    schedule.timeFrames
  );

  if (nextUpcoming) {
    return dayjs.utc(nextUpcoming).valueOf();
  }

  return 0;
};

export const applySorting = (
  offers: Offer[],
  sorting: ISortingWithNullable
): Offer[] => {
  if (sorting.sortValue === null) {
    return offers;
  }

  if (sorting.sortValue === SortingOffersValue.STATUS) {
    return sortOffersByStatus(offers, sorting.direction);
  }

  if (sorting.sortValue === SortingOffersValue.SCHEDULE) {
    return sortOffersBySchedule(offers, sorting.direction);
  }

  return offers;
};

// --- Badge helpers ---

export const createBadgeFilter = (
  relevantBadgeType: EBadgeType | 'both',
  viewModel: EBundlesViewModel | undefined
): ((badgeType: EBadgeType) => boolean) => {
  return (badgeType: EBadgeType): boolean => {
    if (
      relevantBadgeType === 'both' ||
      ((viewModel === EBundlesViewModel.LINEAR ||
        viewModel === EBundlesViewModel.TWO_IN_ONE ||
        viewModel === EBundlesViewModel.TWO_IN_ONE_DYNAMIC ||
        viewModel === EBundlesViewModel.THREE_IN_ONE ||
        viewModel === EBundlesViewModel.SIX_IN_ONE) &&
        badgeType === relevantBadgeType)
    ) {
      return true;
    }
    return false;
  };
};

const BADGE_LABEL_MAP: Record<string, string> = {
  [EBadgeType.RIBBON]: 'Ribbon',
  [EBadgeType.EMBLEM]: 'Emblem'
};

const BADGE_PLACEHOLDER_MAP: Record<string, string> = {
  [EBadgeType.RIBBON]: 'Select Ribbon Badge',
  [EBadgeType.EMBLEM]: 'Select Emblem Badge'
};

export const getBadgeLabel = (badgeType: EBadgeType | 'both'): string =>
  BADGE_LABEL_MAP[badgeType] || 'Badge';

export const getBadgePlaceholder = (badgeType: EBadgeType | 'both'): string =>
  BADGE_PLACEHOLDER_MAP[badgeType] || 'Select Badge';

export const shouldRenderBadge = (badgeType: EBadgeType | 'both' | undefined): boolean => {
  return (
    badgeType === EBadgeType.RIBBON ||
    badgeType === EBadgeType.EMBLEM ||
    badgeType === 'both'
  );
};

export const getRelevantBadgeType = <T extends string>(
  modelValue: T | undefined,
  modelBadgeMap: Partial<Record<T, EBadgeType | 'both'>>,
  viewModel: EBundlesViewModel | undefined
): EBadgeType | 'both' => {
  if (modelValue && modelBadgeMap[modelValue]) {
    return modelBadgeMap[modelValue]!;
  }

  if (viewModel) {
    return MAP_VIEW_MODEL_TO_BADGE_TYPE[viewModel];
  }

  return EBadgeType.RIBBON;
};

// --- Form validation ---

export const checkUniqueness = (
  value: string | undefined,
  field: keyof Offer,
  offers: Offer[] | undefined,
  currentData: Partial<Offer> | undefined,
  isEdit: boolean
): boolean => {
  const isTaken = offers?.find((o: Offer) => {
    if (!isEdit) {
      return o[field] === value;
    }
    return o[field] === value && currentData?.[field] !== value;
  });
  return !isTaken;
};

// --- Form builders ---

export const buildDynamicOfferUi = (
  values: ISharedFormValues,
  emblemBadges: unknown[],
  ribbonBadges: unknown[]
): IDynamicOfferUi => ({
  badges: [],
  salePercentage: values.salePercentage || 0,
  salePercentageDisplayType: values.salePercentageDisplayType || 'percentage',
  offerBadges: {
    emblems: {
      data: emblemBadges || []
    },
    ribbons: {
      data: ribbonBadges || []
    }
  }
});

export const buildProductSale = (
  values: ISharedFormValues
): IProductSale => ({
  sale: values.salePercentage || 0,
  type: values.salePercentageDisplayType || 'percentage'
});

export const buildPriceDiscount = (
  values: ISharedFormValues
): IPriceDiscount => ({
  discount: values.discount || 0,
  type: values.priceDiscountDisplayType || 'percentage'
});

// --- Shared initial values builders ---

interface IBaseInitialValues {
  publisherOfferId: string;
  name: string;
  displayName: string;
  description?: string;
  segments: string[];
  tags: string[];
  active: boolean | undefined;
}

export const buildBaseInitialValues = (
  data: Partial<Offer>,
  edit: boolean,
  dup: boolean
): IBaseInitialValues => ({
  publisherOfferId: data.publisherOfferId || '',
  name: data.name ? `${data.name}${dup ? '_copy' : ''}` : '',
  displayName: data.displayName
    ? `${data.displayName}${dup ? '_copy' : ''}`
    : data.name
      ? `${data.name}${dup ? '_copy' : ''}`
      : '',
  ...(data.description && { description: data.description }),
  segments: data.segments || [],
  tags: data.tags || [],
  active: edit ? data?.active : true
});

export const buildPriceFromCents = (
  priceInUsdCents: number | undefined,
  fallback: number = MIN_USD_PRICE
): number => {
  if (priceInUsdCents === undefined) {
    return fallback;
  }
  const price = new Decimal(priceInUsdCents).div(100).toNumber();
  return price === 0 ? 0 : price;
};

export const buildOfferUiIdInitialValue = (
  data: Partial<Offer>,
  edit: boolean,
  dup: boolean,
  defaultOfferDesignId: string,
  getOfferResult?: Partial<Offer>
): string => {
  if (edit || dup) {
    if (edit) {
      return data.offerUi?._id || data.offerUi?.offerUiId || '';
    }
    return getOfferResult?.offerUi?.offerUiId || '';
  }
  return defaultOfferDesignId;
};

// --- Shared payload builders ---

export const mapApiProductsToLocal = (
  apiProducts: IApiProduct[]
): IProductProps[] => {
  return apiProducts.map(
    (p: IApiProduct, index: number): IProductProps => ({
      _id: p.product?.productId || String(index),
      productId: p.product?.productId || '',
      image: p.product?.images?.[0]?.url || '',
      imagePrefix:
        p.product?.images?.find(
          (i: { url: string; type?: string }) => i.type === EAssetType.PRODUCT_PREFIX
        )?.url || '',
      name: p.product?.name || '',
      amount: p.quantity,
      type: p.product?.type || '',
      textFontColorHex: p.product?.textFontColorHex || '',
      prefix: p.product?.prefix || '',
      suffix: p.product?.suffix || '',
      publisherProductId: p.product?.publisherProductId,
      quantityDisplay: p.quantityDisplay || '',
      priority: p.priority || EProductPriority.SUB
    })
  );
};

export const buildOfferBadges = (
  badgeId: string | null | undefined,
  publisherBadgeId: string | undefined,
  fallbackPublisherBadgeId: string
): IOfferBadge[] => {
  if (!badgeId) {
    return [];
  }

  return [
    {
      badgeId,
      publisherBadgeId: publisherBadgeId || fallbackPublisherBadgeId
    }
  ];
};

export const buildSchedulePayload = <
  T extends { permanent?: boolean; timeFrames?: unknown[] }
>(
  schedule: T
): T => ({
  ...schedule,
  timeFrames: schedule?.permanent ? [] : schedule?.timeFrames || []
});

export const buildSectionTabPayload = (
  sectionId?: string,
  tabId?: string
): ISectionTabPayload => ({
  sectionId: sectionId && sectionId !== '0' ? sectionId : null,
  tabId: tabId && tabId !== '0' ? tabId : null
});

export const buildBaseOfferPayload = (
  values: {
    publisherOfferId: string;
    name: string;
    displayName?: string;
    description?: string;
    offerUiId: string;
    tags?: string[];
    active?: boolean;
  },
  chosenSegment: string[],
  offerType: OfferType
): IBaseOfferPayload => ({
  publisherOfferId: values.publisherOfferId,
  name: values.name,
  displayName: values.displayName || values.name,
  ...(values.description && { description: values.description }),
  type: offerType,
  active: values.active,
  offerUiId: values.offerUiId,
  segments: chosenSegment,
  tags: values.tags
});

export const clampPriceValue = (
  price: number | undefined
): number => {
  if (!price) {
    return MIN_USD_PRICE;
  }

  if (String(price).length > PRICE_VALUE_LIMIT) {
    return parseFloat(String(price).slice(0, PRICE_VALUE_LIMIT));
  }

  return price;
};

export const extractActiveOfferIds = (
  offers: Offer[] = []
): Array<{ content: string; key: string; value: string }> => {
  return offers
    .filter((offer): boolean => {
      const isActive = offer.active;
      const scheduleStatus = appearanceUtils.getStrongestStatusWithTime(
        offer?.schedule?.timeFrames
      )?.status;

      const isExpiredPromotion =
        offer.type === OfferType.SPECIAL_OFFER &&
        scheduleStatus === EAppearanceStatus.ENDED &&
        !offer?.schedule?.permanent;

      return !!isActive && !isExpiredPromotion;
    })
    .map(
      (offer): { content: string; key: string; value: string } => ({
        content: offer.publisherOfferId,
        key: offer.publisherOfferId,
        value: offer.publisherOfferId
      })
    );
};

// --- Form title ---

export const buildFormTitle = (
  edit: boolean,
  label: string,
  offerName?: string
): string =>
  `${edit ? 'Edit' : 'Create'} ${label}${edit && offerName ? ` (${offerName})` : ''}`;

// --- Preview helpers ---

export const buildProductsListForPreview = (
  products: IProductProps[]
): Array<{
  quantity: string | number | undefined;
  quantityDisplay: string | undefined;
  images: { product: string | undefined; productPrefix: string | undefined };
  textFontColorHex: string | undefined;
  prefix: string | undefined;
  suffix: string | undefined;
  priority: string;
  type: EProductType;
}> => {
  if (products.length === 0) {
    return [];
  }

  return products.map((p) => ({
    quantity: p?.amount,
    quantityDisplay: p?.quantityDisplay,
    images: {
      product: p?.image,
      productPrefix: p?.imagePrefix
    },
    textFontColorHex: p?.textFontColorHex,
    prefix: p?.prefix,
    suffix: p?.suffix,
    priority: p?.priority || EProductPriority.SUB,
    type: 'Quantity' as EProductType
  }));
};

export const buildBootInfo = (
  storeThemeData: unknown,
  featureFlags: Record<string, unknown> | undefined
): { storeTheme: unknown; featureFlags: Record<string, unknown> } => ({
  storeTheme: { ...(storeThemeData as Record<string, unknown>), storeScreen: {} },
  featureFlags: featureFlags || {}
});

// --- Badge initial values ---

export const getInitialBadgeValues = (
  badges: Array<{ badge?: { type?: string; badgeId?: string; publisherBadgeId?: string } }> | undefined,
  filterRelevantBadges: (type: EBadgeType) => boolean
): { badgeId: string | null; publisherBadgeId: string | undefined } => {
  const relevantBadge = badges?.find((b) => {
    if (!b.badge?.type) {
      return false;
    }
    return filterRelevantBadges(b.badge.type as EBadgeType);
  });

  return {
    badgeId: relevantBadge?.badge?.badgeId || null,
    publisherBadgeId: relevantBadge?.badge?.publisherBadgeId
  };
};

// --- Shared form initialValues builders ---

export const buildSaleDiscountInitialValues = (
  data: Partial<Offer>
): {
  salePercentage: number;
  salePercentageDisplayType: ESaleDiscountDisplayType;
  discount: number;
  priceDiscountDisplayType: ESaleDiscountDisplayType;
} => ({
  salePercentage: data?.productSale?.sale || 0,
  salePercentageDisplayType:
    (data.productSale?.type as ESaleDiscountDisplayType) || ESaleDiscountDisplayType.PERCENTAGE,
  discount: data?.priceDiscount?.discount || 0,
  priceDiscountDisplayType:
    (data.priceDiscount?.type as ESaleDiscountDisplayType) || ESaleDiscountDisplayType.PERCENTAGE
});

export const buildSectionTabInitialValues = (
  data: Partial<Offer>
): { section: string; tab: string } => ({
  section: data?.section?.sectionId || data?.sectionId?.sectionId || '0',
  tab: data?.tab?.tabId || data?.tabId?.tabId || '0'
});

export const buildSetAsFreeInitialValue = (
  data: Partial<Offer>,
  edit: boolean,
  dup: boolean
): boolean =>
  (edit || dup) &&
  data.productsSequence?.[0]?.priceInUsdCents !== undefined &&
  buildPriceFromCents(data.productsSequence[0].priceInUsdCents, 0) === 0;

export const buildScheduleInitialValue = (
  data: Partial<Offer>
): NonNullable<Offer['schedule']> =>
  data.schedule || { permanent: true, timeFrames: [] };

export const buildEmblemBadgeInitialValues = (
  data: Partial<Offer>
): { leftBadge: string; rightBadge: string } => ({
  leftBadge:
    data?.badges?.find(
      (b) => b?.badge?.position === 'left' && b.badge?.type === 'emblem'
    )?.badge?.badgeId || '',
  rightBadge:
    data?.badges?.find(
      (b) => b?.badge?.position === 'right' && b.badge?.type === 'emblem'
    )?.badge?.badgeId || ''
});

export const buildProgressBarInitialValues = (
  data: Partial<Offer>
): { progressBarPoints: number; progressBarId: string } => ({
  progressBarPoints:
    data?.productsSequence?.[0]?.progressBarPoints?.[0]?.points || 0,
  progressBarId:
    data?.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || ''
});
